/**
 * Spell-check word lists (I-3006): the rules of a dictionary word and the file
 * format of „Importieren“ / „Exportieren“. The settings' dictionary page
 * („Allgemein → Wörterbuch“) and the admin app's company dictionary
 * (Firmenwörterbuch) both use this — one set of rules for both bundles, so it
 * lives in the shared package (`config/spellCheck.ts` builds on it). Pure functions,
 * no Vue; reached through `@blpde/connect/config`.
 *
 * Read: a word list (`.txt`, one word per line) or a dictionary of Hunspell or
 * Word (`.dic`): a leading line with nothing but the word count is skipped,
 * a word's affix flags (`Wort/AB`) and morphological fields after a tab are
 * cut off, blank lines and comments (`#`) are left out. The bytes may be
 * UTF-8, UTF-16 with a byte order mark (Word writes its `custom.dic` that
 * way) or, when they are no valid UTF-8, Windows-1252 (older Hunspell files).
 *
 * Written: UTF-8, one word per line, sorted the way a German reader expects.
 */

/** The longest word a dictionary takes. */
export const DICTIONARY_MAX_WORD = 100;

/** The largest file the import reads (a dictionary of a few hundred thousand words). */
export const DICTIONARY_FILE_MAX_BYTES = 8 * 1024 * 1024;

/** The languages of the base vocabulary the dictionary's corrections, never-suggest words and synonyms are kept per. */
export const DICTIONARY_LANGUAGES = ['de', 'en'] as const;

export type DictionaryLanguage = (typeof DICTIONARY_LANGUAGES)[number];

/** The largest document one layer of one key may hold (master's `maxDocumentBytes`). */
export const DICTIONARY_DOCUMENT_MAX_BYTES = 1024 * 1024;

/**
 * A word as a dictionary keeps it — the server's word rule: trimmed, one line,
 * no NUL, at most DICTIONARY_MAX_WORD characters (runes); undefined when it
 * is none of that.
 */
export function dictionaryWord(word: string): string | undefined {
  const trimmed = word.trim();
  if (!trimmed || /[\r\n\u0000]/.test(trimmed)) return undefined;
  if (Array.from(trimmed).length > DICTIONARY_MAX_WORD) return undefined;
  return trimmed;
}

/** The bytes a layer's document takes as master stores it (JSON, UTF-8). */
export function dictionaryDocumentBytes(document: unknown): number {
  return new TextEncoder().encode(JSON.stringify(document ?? {})).length;
}

/** Words sorted the way a German reader expects (a new array). */
export function sortDictionaryWords(words: Iterable<string>): string[] {
  return [...words].sort((a, b) => a.localeCompare(b, 'de'));
}

/** What adding several words to a dictionary amounts to. */
export interface DictionaryAddition {
  /** The new words as a merge patch of the word map (`{ word: true }`); empty when none is new. */
  change: Record<string, true>;
  /** The words the change adds, in the order given. */
  added: string[];
  /** Words left out: already in the dictionary, given twice or no word the dictionary takes. */
  skipped: number;
}

/** Adds `candidates` to a dictionary that holds `existing`: one change for all of them. */
export function dictionaryAddition(existing: Iterable<string>, candidates: readonly string[]): DictionaryAddition {
  const known = new Set(existing);
  const change: Record<string, true> = {};
  const added: string[] = [];
  let skipped = 0;
  for (const candidate of candidates) {
    const word = dictionaryWord(candidate);
    if (!word || known.has(word)) {
      skipped += 1;
      continue;
    }
    known.add(word);
    change[word] = true;
    added.push(word);
  }
  return { change, added, skipped };
}

/** The words a dictionary file holds. */
export interface DictionaryFileWords {
  /** Valid words, trimmed, each once, in file order. */
  words: string[];
  /** Entries that are no word the dictionary takes (too long). */
  invalid: number;
  /** Entries the file holds more than once. */
  repeated: number;
}

/** The file's bytes as text: a byte order mark decides, else UTF-8, else Windows-1252. */
export function decodeDictionaryFile(bytes: Uint8Array): string {
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes.subarray(2));
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes.subarray(2));
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) return new TextDecoder('utf-8').decode(bytes.subarray(3));
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/** One line's word: the part before the first tab and the first unescaped `/` (Hunspell's flags). */
function entryOf(line: string): string {
  const field = line.split('\t', 1)[0]!;
  let word = '';
  for (let at = 0; at < field.length; at += 1) {
    const char = field[at]!;
    if (char === '\\' && field[at + 1] === '/') {
      word += '/';
      at += 1;
      continue;
    }
    if (char === '/') break;
    word += char;
  }
  return word.trim();
}

/** The words of a dictionary file's text. */
export function parseDictionaryFile(text: string): DictionaryFileWords {
  const lines = text.replace(/^﻿/, '').split(/\r\n|\n|\r/);
  const words: string[] = [];
  const seen = new Set<string>();
  let invalid = 0;
  let repeated = 0;
  let first = true;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    // Hunspell's `.dic` starts with the number of words that follow.
    if (first && /^\d+$/.test(line)) {
      first = false;
      continue;
    }
    first = false;
    const entry = entryOf(line);
    if (!entry) continue;
    const word = dictionaryWord(entry);
    if (!word) {
      invalid += 1;
      continue;
    }
    if (seen.has(word)) {
      repeated += 1;
      continue;
    }
    seen.add(word);
    words.push(word);
  }
  return { words, invalid, repeated };
}

/** The export: one word per line, sorted for a German reader, ending in a line break. */
export function dictionaryFileText(words: Iterable<string>): string {
  const sorted = sortDictionaryWords(new Set(words));
  return sorted.length ? `${sorted.join('\n')}\n` : '';
}

/* ── CSV of corrections and synonym groups (I-3024) ──────────────────────── */

/** Why a row of an imported file cannot be taken as it stands. */
export type DictionaryRowProblem =
  | 'columns'
  | 'word_rule'
  | 'language'
  | 'direction'
  | 'terms_count';

/** The fewest and the most terms a synonym group holds. */
export const SYNONYM_MIN_TERMS = 2;
export const SYNONYM_MAX_TERMS = 50;

/** One correction row of a file: `falsch;richtig;sprache`. */
export interface CorrectionRow {
  /** 1-based line of the file. */
  line: number;
  typed: string;
  to: string;
  lang: DictionaryLanguage;
  problem?: DictionaryRowProblem;
}

/** One synonym group row of a file: `name;sprache;richtung;begriff1|begriff2|…`. */
export interface SynonymRow {
  line: number;
  name: string;
  lang: DictionaryLanguage;
  oneWay: boolean;
  /** The terms in file order; `terms[0]` is the source of a one-way group. */
  terms: string[];
  problem?: DictionaryRowProblem;
}

/** The rows of a CSV file (header recognised and dropped, blank lines left out). */
export interface DictionaryCsv<Row> {
  rows: Row[];
  /** The rows that carry a problem. */
  refused: number;
}

const CSV_BOM = /^\uFEFF/;

/** The separator of the first line that has one: `;` (also when both occur), else `,`; `;` for a file without. */
function csvDelimiter(text: string): ';' | ',' {
  for (const line of text.split(/\r\n|\n|\r/)) {
    if (line.includes(';')) return ';';
    if (line.includes(',')) return ',';
  }
  return ';';
}

/** The cells of one CSV line; double quotes quote a cell, `""` is a quote inside it. */
function csvCells(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let cell = '';
  let quoted = false;
  for (let at = 0; at < line.length; at += 1) {
    const char = line[at]!;
    if (quoted) {
      if (char === '"' && line[at + 1] === '"') {
        cell += '"';
        at += 1;
      } else if (char === '"') quoted = false;
      else cell += char;
    } else if (char === '"' && cell === '') quoted = true;
    else if (char === delimiter) {
      cells.push(cell);
      cell = '';
    } else cell += char;
  }
  cells.push(cell);
  return cells.map((value) => value.trim());
}

/** The non-blank, non-comment lines of a file with their 1-based line numbers. */
function csvLines(text: string): Array<{ line: number; text: string }> {
  return text
    .replace(CSV_BOM, '')
    .split(/\r\n|\n|\r/)
    .map((raw, index) => ({ line: index + 1, text: raw }))
    .filter((entry) => entry.text.trim() !== '' && !entry.text.trim().startsWith('#'));
}

function languageOf(cell: string | undefined, fallback: DictionaryLanguage): DictionaryLanguage | undefined {
  const value = (cell ?? '').trim().toLowerCase();
  if (!value) return fallback;
  return (DICTIONARY_LANGUAGES as readonly string[]).includes(value) ? (value as DictionaryLanguage) : undefined;
}

const CORRECTION_HEADERS = new Set(['falsch', 'typed', 'wrong', 'term']);
const SYNONYM_HEADERS = new Set(['name', 'gruppe', 'group']);

/**
 * The corrections of a CSV file: `falsch;richtig;sprache`, the header and
 * the language column optional, `;` or `,` as the separator. A language other
 * than `de`/`en` and a word that is no word carry a `problem`; an empty
 * language is `fallback`.
 */
export function parseCorrectionsCsv(text: string, fallback: DictionaryLanguage = 'de'): DictionaryCsv<CorrectionRow> {
  const delimiter = csvDelimiter(text);
  const lines = csvLines(text);
  const rows: CorrectionRow[] = [];
  lines.forEach((entry, index) => {
    const cells = csvCells(entry.text, delimiter);
    if (index === 0 && CORRECTION_HEADERS.has((cells[0] ?? '').toLowerCase())) return;
    const lang = languageOf(cells[2], fallback);
    const typed = dictionaryWord(cells[0] ?? '');
    const to = dictionaryWord(cells[1] ?? '');
    const row: CorrectionRow = {
      line: entry.line,
      typed: typed ?? (cells[0] ?? ''),
      to: to ?? (cells[1] ?? ''),
      lang: lang ?? fallback,
    };
    if (cells.length < 2) row.problem = 'columns';
    else if (!typed || !to) row.problem = 'word_rule';
    else if (!lang) row.problem = 'language';
    rows.push(row);
  });
  return { rows, refused: rows.filter((row) => row.problem).length };
}

const TWO_WAY = new Set(['beidseitig', 'two_way', 'two-way', 'twoway']);
const ONE_WAY = new Set(['einseitig', 'one_way', 'one-way', 'oneway']);

/**
 * The synonym groups of a CSV file: `name;sprache;richtung;begriff1|begriff2|…`
 * (`richtung`: `beidseitig`/`einseitig`, also `two_way`/`one_way`; empty is
 * two-way; one-way takes the first term as the source). The language column
 * and the header are optional in the sense that an empty language is
 * `fallback`; the four columns stay.
 */
export function parseSynonymsCsv(text: string, fallback: DictionaryLanguage = 'de'): DictionaryCsv<SynonymRow> {
  const delimiter = csvDelimiter(text);
  const lines = csvLines(text);
  const rows: SynonymRow[] = [];
  lines.forEach((entry, index) => {
    const cells = csvCells(entry.text, delimiter);
    if (index === 0 && SYNONYM_HEADERS.has((cells[0] ?? '').toLowerCase())) return;
    const lang = languageOf(cells[1], fallback);
    const direction = (cells[2] ?? '').trim().toLowerCase();
    const name = dictionaryWord(cells[0] ?? '');
    const raw = (cells[3] ?? '').split('|');
    const terms = raw.map((value) => dictionaryWord(value)).filter((value): value is string => !!value);
    const row: SynonymRow = {
      line: entry.line,
      name: name ?? (cells[0] ?? ''),
      lang: lang ?? fallback,
      oneWay: ONE_WAY.has(direction),
      terms,
    };
    if (cells.length < 4) row.problem = 'columns';
    else if (!name || raw.some((value) => value.trim() !== '' && !dictionaryWord(value))) row.problem = 'word_rule';
    else if (!lang) row.problem = 'language';
    else if (direction && !TWO_WAY.has(direction) && !ONE_WAY.has(direction)) row.problem = 'direction';
    else if (terms.length < SYNONYM_MIN_TERMS || terms.length > SYNONYM_MAX_TERMS) row.problem = 'terms_count';
    rows.push(row);
  });
  return { rows, refused: rows.filter((row) => row.problem).length };
}

/** A CSV cell: quoted when it holds the separator, a quote or a line break. */
function csvCell(value: string): string {
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** The export of corrections: `falsch;richtig;sprache`, sorted by language and word. */
export function correctionsCsvText(rows: Iterable<{ typed: string; to: string; lang: DictionaryLanguage }>): string {
  const sorted = [...rows].sort((a, b) => a.lang.localeCompare(b.lang) || a.typed.localeCompare(b.typed, 'de'));
  const lines = ['falsch;richtig;sprache', ...sorted.map((row) => [row.typed, row.to, row.lang].map(csvCell).join(';'))];
  return `${lines.join('\n')}\n`;
}

/** The export of synonym groups: `name;sprache;richtung;begriff1|begriff2|…`, sorted by language and name. */
export function synonymsCsvText(
  rows: Iterable<{ name: string; lang: DictionaryLanguage; oneWay: boolean; terms: readonly string[] }>,
): string {
  const sorted = [...rows].sort((a, b) => a.lang.localeCompare(b.lang) || a.name.localeCompare(b.name, 'de'));
  const lines = [
    'name;sprache;richtung;begriffe',
    ...sorted.map((row) =>
      [row.name, row.lang, row.oneWay ? 'einseitig' : 'beidseitig', row.terms.join('|')].map(csvCell).join(';'),
    ),
  ];
  return `${lines.join('\n')}\n`;
}
